import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './_styles_/Countries.css';
import { ALL_COUNTRIES, ON_COUNTRY_CHANGED } from '../../graphql/countries';
import CountriesList from './CountriesList';
import CreateCountryRow from './CreateCountryRow';
import { showInfoNotification } from '../../actions/notifications';
import Paginator from '../Paginator';
import {
    parseIncludeDeactivated,
    parsePaging,
    parseSorting,
} from '../../utils/queryString';
import Spinner from '../Spinner';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import SubscriptionActions from '../../utils/SubscriptionActions';
import Constants from '../../utils/Constants';
import Error from '../errors/Error';
import CheckboxField from '../fields/CheckboxField';
import { getValueMapping } from './CountryRow';
import Privileges from '../../utils/Privileges';
import useAuthentication from '../../hooks/useAuthentication';
import { hasPrivilege } from '../../hocs/withPrivileges';

export function createNotificationText(t, action, name, code) {
    let notification;
    switch (action) {
        case SubscriptionActions.COUNTRY_CREATED:
            notification = t('countries.notification.country-create-thirdparty', { name, code });
            break;
        case SubscriptionActions.COUNTRY_UPDATED:
            notification = t('countries.notification.country-update-thirdparty', { name, code });
            break;
        default:
            notification = t('countries.notification.countries-modified-thirdparty');
    }
    return notification;
}

function Countries() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'code',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const includeDeactivated = parseIncludeDeactivated(location.search);
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        includeDeactivated,
        sortKey,
        sortDirection,
    });
    const {
        loading,
        error,
        data,
        refetch,
    } = useExtendedQuery(ALL_COUNTRIES, {
        variables: refetchProps,
        valueMappingFn: getValueMapping,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    const onFilter = (newIncludeDeactivated) => setRefetchProps({
        ...refetchProps,
        includeDeactivated: newIncludeDeactivated,
        page: 0,
    });
    useExtendedSubscription(ON_COUNTRY_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'countryChanged',
        onChangeCallback: ({ action, model: { code, name } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.COUNTRY_EDIT])) {
                const notification = createNotificationText(t, action, name, code);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result;
    if (loading) {
        result = <Spinner className="countries-loading" />;
    } else if (error) {
        result = <Error className="countries-error" error={error} />;
    } else {
        result = (
            <Paginator
                totalCount={data.allCountries.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <CountriesList
                    countries={data.allCountries.models}
                    onSort={onSort}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                />
            </Paginator>
        );
    }
    return (
        <>
            <h4><FontAwesomeIcon icon="globe" />&nbsp;{t('title.countries')}</h4>
            <CreateCountryRow />
            <div className="search-and-filter countries">
                <div className="filter">
                    <CheckboxField
                        label={t('countries.include-deactivated')}
                        value={refetchProps.includeDeactivated}
                        onChange={onFilter}
                        className="countries--search-and-filter--include-deactivated"
                        inputId="countries--search-and-filter--include-deactivated"
                    />
                </div>
            </div>
            {result}
        </>
    );
}

export default Countries;
